import { graphqlClient } from '@/services/core/graphqlClient';
import { GRAPHQL_RESOURCES } from '@/services/resources';

// Type definitions for appointments
export interface AppointmentSlot {
    id: string;
    startTime: string;
    endTime: string;
    isAvailable: boolean;
    providerId?: string;
    clinicId?: string;
}

export interface Appointment {
    id: string;
    patientId: string;
    providerId: string;
    clinicId: string;
    startTime: string;
    endTime: string;
    status: string;
    type: string;
    notes?: string;
}

export interface TimeSlot {
    id: string;
    startTime: string;
    endTime: string;
    duration: number;
    isBooked: boolean;
}

export interface Provider {
    id: string;
    name: string;
    specialty: string;
    clinicId: string;
    isActive: boolean;
}

/**
 * AppointmentsGraphqlService - Handles all appointment and scheduling related GraphQL operations
 * Consumes organized queries from appointmentsGquery.ts
 */
export class AppointmentsGraphqlService {
    /**
     * Get available appointment slots for a provider
     */
    async getAppointmentDates(facilityId: number, hasuraToken: string): Promise<any[]> {
      const result = await graphqlClient.query(
        GRAPHQL_RESOURCES.GET_APPOINTMENT_DATES,
        { facilityId },
        hasuraToken
      );
      return result.data?.pep_vw_available_clinic_appointments || [];
    }
  
    /**
     * Get appointment times for a specific facility and date
     */
    async getAppointmentTimes(facilityId: number, appointmentDate: string, hasuraToken: string): Promise<any[]> {
      const result = await graphqlClient.query(
        GRAPHQL_RESOURCES.GET_APPOINTMENT_TIMES,
        { facilityId, appointmentDate },
        hasuraToken
      );
      return result.data?.pep_vw_available_clinic_appointments || [];
    }
  
    /**
     * Get appointment facilities for a clinic
     */
    async getAppointmentFacilities(clinicId: number, hasuraToken: string): Promise<any[]> {
      const result = await graphqlClient.query(
        GRAPHQL_RESOURCES.GET_APPOINTMENT_FACILITIES,
        { clinicId },
        hasuraToken
      );
      return result.data?.pep_vw_consolidated_data || [];
    }
  
    /**
     * Get simple appointment dates
     */
    async getAppointmentDatesSimple(facilityId: number): Promise<any[]> {
      const result = await graphqlClient.query(
        GRAPHQL_RESOURCES.GET_APPOINTMENT_DATES_SIMPLE,
        { facilityId }
      );
      return result.data?.pep_vw_appointment_dates || [];
    }
  
    /**
     * Get simple appointment times
     */
    async getAppointmentTimesSimple(facilityId: number, appointmentDate: string): Promise<any[]> {
      const result = await graphqlClient.query(
        GRAPHQL_RESOURCES.GET_APPOINTMENT_TIMES_SIMPLE,
        { facilityId, appointmentDate }
      );
      return result.data?.pep_vw_appointment_times || [];
    }
  
    /**
     * Get all appointment slots without filter
     */
    async getAllAppointmentSlots(hasuraToken: string): Promise<any[]> {
      const result = await graphqlClient.query(
        GRAPHQL_RESOURCES.APPOINTMENT_SLOTS_APPOINTMENTS_ALL,
        {},
        hasuraToken
      );
      return result.data?.pep_vw2_clinic_appointments || [];
    }
  
    /**
     * Get filtered appointment slots by clinic
     */
    async getFilteredAppointmentSlots(clinicID: number, hasuraToken: string): Promise<any[]> {
      const result = await graphqlClient.query(
        GRAPHQL_RESOURCES.APPOINTMENT_SLOTS_APPOINTMENTS_FILTERED,
        { clinicID },
        hasuraToken
      );
      return result.data?.pep_vw2_clinic_appointments || [];
    }

    /**
     * Get appointment slots with advanced filtering
     */
      async getAdvancedFilteredAppointmentSlots(
        hasuraToken: string,
        filters: {
          clinicID?: number | null;
          facilityID?: number | null;
          facilityIDs?: number[] | null;
          startDate?: string;
          endDate?: string;
          startTime?: string;
          endTime?: string;
        }
      ): Promise<any[]> {
        // Convert time to 24-hour format with seconds
        const convertTo24HourFormat = (time: string): string => {
          if (!time) return time;
          
          // If already in HH:MM format, add seconds
          if (time.includes(':') && !time.includes(' ')) {
            return time.includes(':') && time.split(':').length === 2 ? `${time}:00` : time;
          }
          
          return time;
        };

        // Handle facility IDs - support both single ID and array of IDs
        let facilityIDs: number[] | null = null;
        if (filters.facilityIDs && filters.facilityIDs.length > 0) {
          facilityIDs = filters.facilityIDs;
        } else if (filters.facilityID) {
          facilityIDs = [filters.facilityID];
        }

        // Build dynamic query based on available filters
        const whereConditions: any[] = [];

        // Add clinic filter if provided
        if (filters.clinicID) {
          whereConditions.push(`{clinic_id: {_eq: ${filters.clinicID}}}`);
        }

        // Add facility filter if provided
        if (facilityIDs && facilityIDs.length > 0) {
          whereConditions.push(`{facility_id: {_in: [${facilityIDs.join(', ')}]}}`);
        }

        // Add date filters if provided
        if (filters.startDate) {
          whereConditions.push(`{appointment_date: {_gte: "${filters.startDate}"}}`);
        }
        if (filters.endDate) {
          whereConditions.push(`{appointment_date: {_lte: "${filters.endDate}"}}`);
        }

        // Add time filters if provided
        if (filters.startTime) {
          const startTime24 = convertTo24HourFormat(filters.startTime);
          whereConditions.push(`{appointment_start_time: {_gte: "${startTime24}"}}`);
        }
        if (filters.endTime) {
          const endTime24 = convertTo24HourFormat(filters.endTime);
          whereConditions.push(`{appointment_end_time: {_lte: "${endTime24}"}}`);
        }

        // Build the dynamic query
        const whereClause = whereConditions.length > 0 ? `_and: [${whereConditions.join(', ')}]` : '';

        const query = `
          query GetFilteredAppointments {
            pep_vw2_clinic_appointments(
              where: {
                ${whereClause}
              },
              order_by: {appointment_date: desc}
            ) {
              appointment_id
              clinic_name
              facility_name
              appointment_date
              appointment_start_time
              appointment_end_time
              patient_clinic_data_id
              clinic_id
              facility_id
            }
          }
        `;

        const result = await graphqlClient.query(query, {}, hasuraToken);
        return result.data?.pep_vw2_clinic_appointments || [];
      }

    /**
     * Get appointment details for an activity
     */
    async getActivityAppointmentDetails(activityId: number, hasuraToken: string): Promise<any[]> {
      const result = await graphqlClient.query(
        GRAPHQL_RESOURCES.GET_ACTIVITY_APPOINTMENT_DETAILS,
        { activityId },
        hasuraToken
      );
      return result.data?.pep_vw_appointment_details || [];
    }

    /**
     * Get available appointment details for enrollment
     */
    async getAppointmentDetailsForEnrollment(hasuraToken: string, where?: any): Promise<any[]> {
      const result = await graphqlClient.query(
        GRAPHQL_RESOURCES.GET_APPOINTMENT_DETAILS,
        { where: where || {} },
        hasuraToken
      );
      return result.data?.pep_vw_appointment_details || [];
    }
    /*
     * Get total count of appointments with optional filters
     */
    async getAppointmentSlotsCount(
      hasuraToken: string,
      filters: {
        clinicID?: number | null;
        facilityIDs?: number[] | null;
        startDate?: string;
        endDate?: string;
      }
    ): Promise<number> {
      const clinicFilter = filters.clinicID ? { clinic_id: { _eq: filters.clinicID } } : {};
      const facilityFilter = filters.facilityIDs && filters.facilityIDs.length > 0 
        ? { facility_id: { _in: filters.facilityIDs } } 
        : {};
      const dateFilter = {
        ...(filters.startDate && { appointment_date: { _gte: filters.startDate } }),
        ...(filters.endDate && { appointment_date: { _lte: filters.endDate } })
      };

      const result = await graphqlClient.query(
        GRAPHQL_RESOURCES.APPOINTMENT_SLOTS_COUNT_AGGREGATE,
        {
          clinicFilter,
          facilityFilter,
          dateFilter,
          timeFilter: {}
        },
        hasuraToken
      );
      
      return result.data?.pep_vw2_clinic_appointments_aggregate?.aggregate?.count || 0;
    }

    /**
     * Get paginated appointment slots
     */
    async getPaginatedAppointmentSlots(
      hasuraToken: string,
      limit: number,
      offset: number,
      filters: {
        clinicID?: number | null;
        facilityIDs?: number[] | null;
        startDate?: string;
        endDate?: string;
      }
    ): Promise<any[]> {
      const clinicFilter = filters.clinicID ? { clinic_id: { _eq: filters.clinicID } } : {};
      const facilityFilter = filters.facilityIDs && filters.facilityIDs.length > 0 
        ? { facility_id: { _in: filters.facilityIDs } } 
        : {};
      const dateFilter = {
        ...(filters.startDate && { appointment_date: { _gte: filters.startDate } }),
        ...(filters.endDate && { appointment_date: { _lte: filters.endDate } })
      };

      const result = await graphqlClient.query(
        GRAPHQL_RESOURCES.APPOINTMENT_SLOTS_APPOINTMENTS_PAGINATED,
        {
          limit,
          offset,
          clinicFilter,
          facilityFilter,
          dateFilter,
          timeFilter: {}
        },
        hasuraToken
      );
      
      return result.data?.pep_vw2_clinic_appointments || [];
    }
}

// Export singleton instance
export const appointmentsGraphqlService = new AppointmentsGraphqlService();